/**
 * Web clipper (ADR 0022): a clipped web page (from the browser extension or the
 * share sheet) becomes a page under "Webclips", with the source as a property
 * and a link at the top.
 */
import type { JSONContent } from '@tiptap/core';
import { ensureTaskIds } from '@nexus/editor';
import { parseMarkdown } from '@nexus/markdown';
import type { PageRecord, Store } from '@nexus/store';
import { jsonToUpdate, reindexPage } from './content';
import { ensureSpecialPage } from './tasks';

export const CLIPS_FOLDER = 'Webclips';

export interface Clip {
  title: string;
  url?: string | null;
  markdown?: string | null;
  note?: string | null;
  /** When it was clipped (ms); defaults to now. */
  clippedAt?: number;
}

export const clipsFolder = (store: Store) =>
  ensureSpecialPage(store, 'clips_parent_id', CLIPS_FOLDER, '📎');

const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();

/** Markdown of the clip page: the note, the source link, then the clipped text. */
export function clipMarkdown(clip: Clip): string {
  const parts: string[] = [];
  if (clip.note?.trim()) parts.push(clip.note.trim());
  if (clip.url) parts.push(`Bron: [${oneLine(clip.title).replace(/[[\]]/g, '')}](${clip.url})`);
  if (clip.markdown?.trim()) {
    // Never a leading "---": the parser would read it as front matter.
    if (parts.length) parts.push('---');
    parts.push(clip.markdown.trim());
  }
  return parts.join('\n\n');
}

export async function clipToPage(store: Store, clip: Clip): Promise<PageRecord> {
  const folder = await clipsFolder(store);
  const title = oneLine(clip.title).slice(0, 200) || clip.url || 'Webclip';
  const page = await store.createPage({ title, parentId: folder.id });
  if (clip.url) await store.setProp(page.id, 'source', clip.url);
  const at = new Date(clip.clippedAt ?? Date.now());
  const day = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
  await store.setProp(page.id, 'clipped', day);
  const md = clipMarkdown(clip);
  if (md) {
    const doc: JSONContent = ensureTaskIds(parseMarkdown(md).doc).doc;
    await store.appendContent(page.id, jsonToUpdate(doc));
    await reindexPage(store, page.id, doc);
  }
  return page;
}
